/* global HTMLDivElement, KeyboardEvent */
// Weekly/Monthly Review Modal (D30, P2-03).
// props: { api, scope: 'week'|'month', onClose }
// Week  → 4 steps: inbox-zero → goals/projects → reflection → summary+save
// Month → 2 steps: reflection → summary+save
import { useEffect, useRef, useState } from "react";
import { BarChart2, X } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import { currentWeekId, currentMonthId, weekStartIso, weekStats, monthStats } from "./review-derive";
import { todayIso } from "../lib/date";
import { aiAvailable } from "../lib/ai/ai-config";
import { InboxZeroStep, ReflectionStep, SummaryStep } from "./weekly-review-steps";
import { GoalsProjectsStep } from "./weekly-review-goals-step";
import { AiReviewDraftButton } from "./ai-review-draft-button";
import { buildReviewDraft } from "./review-draft-derive";
import { useFocusTrap } from "../lib/focus-trap";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  api: LifeOsApi;
  scope: "week" | "month";
  onClose: () => void;
}

const WEEK_STEPS = ["Inbox", "Mục tiêu", "Chiêm nghiệm", "Tổng kết"];
const MONTH_STEPS = ["Chiêm nghiệm", "Tổng kết"];

// ── Main component ─────────────────────────────────────────────────────────────

export function WeeklyReviewModal({ api, scope, onClose }: Props) {
  const today = todayIso();
  const steps = scope === "week" ? WEEK_STEPS : MONTH_STEPS;
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Escape closes the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const [step, setStep] = useState(0);
  const [inboxCleared, setInboxCleared] = useState(false);
  const [reflection, setReflection] = useState("");
  const [wins, setWins] = useState("");
  const [blockers, setBlockers] = useState("");
  // Pending next-action edits from step 2 — flushed on save
  const [nextActions, setNextActions] = useState<Record<string, string>>({});

  const wStart = weekStartIso(today);
  // Scope-correct stats: month review must aggregate the whole month, not the current week.
  const stats = scope === "week" ? weekStats(api.state, wStart) : monthStats(api.state, currentMonthId(today));
  const isLast = step === steps.length - 1;

  // Reflection step index differs by scope
  const reflectionStepIdx = scope === "week" ? 2 : 0;
  const canNext = step === reflectionStepIdx
    ? reflection.trim().length > 0
    : true;

  function handleUpdateNextAction(projectId: string, value: string) {
    setNextActions((prev) => ({ ...prev, [projectId]: value }));
  }

  function handleSave() {
    for (const [projectId, nextAction] of Object.entries(nextActions)) {
      api.actions.updateProject(projectId, { nextAction });
    }
    const id = scope === "week" ? currentWeekId(today) : currentMonthId(today);
    const date = scope === "week" ? wStart : `${currentMonthId(today)}-01`;
    api.actions.saveWeeklyReview({
      id,
      scope,
      date,
      reflection: reflection.trim(),
      wins: wins.trim() || undefined,
      blockers: blockers.trim() || undefined,
      inboxCleared: scope === "week" ? inboxCleared : undefined,
      createdAt: new Date().toISOString(),
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="weekly-review-title"
    >
      <div
        ref={panelRef}
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-brand-100 text-brand-600">
            <BarChart2 className="h-5 w-5" />
          </span>
          <div>
            <h2 id="weekly-review-title" className="font-display text-[18px] font-bold">
              {scope === "week" ? "Review tuần" : "Review tháng"}
            </h2>
            <p className="text-[12px] text-ink-500">
              Bước {step + 1}/{steps.length} · {steps[step]}
            </p>
          </div>
          <button
            className="ml-auto rounded-xl p-1.5 hover:bg-ink-100"
            onClick={onClose}
            aria-label="Đóng"
          >
            <X className="h-4 w-4 text-ink-500" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="mb-5 flex gap-1.5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                i <= step ? "bg-brand-500" : "bg-ink-200"
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="min-h-[180px]">
          {scope === "week" && step === 0 && (
            <InboxZeroStep api={api} onInboxCleared={setInboxCleared} />
          )}
          {scope === "week" && step === 1 && (
            <GoalsProjectsStep
              state={api.state}
              onUpdateNextAction={handleUpdateNextAction}
            />
          )}
          {step === reflectionStepIdx && (
            <div className="space-y-3">
              {/* P6-5: deterministic stats draft — always available (no AI needed). */}
              <button
                onClick={() => {
                  const d = buildReviewDraft(api.state, scope, today);
                  setReflection(d.reflection);
                  setWins(d.wins);
                  setBlockers(d.blockers);
                }}
                className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-ink-200 bg-ink-50 py-2 text-[13px] font-bold text-ink-700 transition hover:bg-ink-100"
              >
                📊 Tóm tắt số liệu {scope === "week" ? "tuần" : "tháng"} (tự điền nháp)
              </button>
              {aiAvailable(api) && (
                <AiReviewDraftButton
                  api={api}
                  scope={scope}
                  onResult={(d) => { setReflection(d.reflection); setWins(d.wins); setBlockers(d.blockers); }}
                />
              )}
              <ReflectionStep
                scope={scope}
                reflection={reflection}
                wins={wins}
                blockers={blockers}
                onReflection={setReflection}
                onWins={setWins}
                onBlockers={setBlockers}
              />
            </div>
          )}
          {isLast && (
            <SummaryStep scope={scope} stats={stats} reflection={reflection} />
          )}
        </div>

        {/* Footer */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="rounded-xl border border-ink-200 px-4 py-2 text-[13px] font-semibold hover:bg-ink-50 disabled:opacity-30"
          >
            Quay lại
          </button>
          <div className="flex gap-2">
            {!isLast && (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext}
                className="gbrand rounded-xl px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40"
              >
                Tiếp theo
              </button>
            )}
            {isLast && (
              <button
                onClick={handleSave}
                disabled={reflection.trim().length === 0}
                className="gbrand rounded-xl px-5 py-2 text-[13px] font-bold text-white disabled:opacity-40"
              >
                Lưu review
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
